// Browser-QA evidence components, shared by the QA Workspace (per-item
// badges) and the Run detail page (per-run verdict panel).

import { useEffect, useRef, useState } from "react";
import api from "../../api";
import Icon from "./icons";
import { relativeTime } from "./shared";

// Evidence video — blob-fetched on demand through the authenticated api
// client (a bare <video src> cross-origin would not carry the session), then
// played from an object URL.
export const EvidenceVideo = ({ runId, file }) => {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  // Monotonic sequence guarding the async load: a fetch that resolves after
  // unmount or after the props changed revokes its blob and sets no state
  // (no object-URL leak, no setState-after-unmount warning).
  const seqRef = useRef(0);
  // A newer execution (new runId/file) resets back to the button state; the
  // [src]-keyed cleanup below revokes the replaced blob.
  useEffect(() => {
    seqRef.current += 1;
    setSrc(null);
    setErr(null);
  }, [runId, file]);
  useEffect(() => {
    return () => {
      if (src) URL.revokeObjectURL(src);
    };
  }, [src]);
  useEffect(() => {
    return () => {
      seqRef.current += 1; // unmount: invalidate any in-flight load
    };
  }, []);
  const load = async () => {
    const seq = ++seqRef.current;
    setLoading(true);
    setErr(null);
    try {
      const res = await api.bssQaArtifact(runId, file);
      const url = URL.createObjectURL(res.data);
      if (seq !== seqRef.current) {
        URL.revokeObjectURL(url);
        return;
      }
      setSrc(url);
    } catch {
      if (seq === seqRef.current) setErr("could not load video");
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  };
  if (src) {
    return (
      <video
        controls
        src={src}
        style={{ width: "100%", maxWidth: 560, borderRadius: 8, marginTop: 6 }}
      />
    );
  }
  return (
    <button
      className="qa-reset"
      onClick={load}
      disabled={loading}
      type="button"
    >
      <Icon.PlayCircle size={13} /> {loading ? "Loading…" : "Evidence video"}
      {err && (
        <span style={{ color: "var(--bss-danger)", marginLeft: 6 }}>{err}</span>
      )}
    </button>
  );
};

export const AUTO_VERDICT_COLOR = {
  pass: "var(--bss-success)",
  fail: "var(--bss-danger)",
  blocked: "var(--bss-warning)",
  human_review: "var(--bss-phase-reviewing)"
};

// Latest automated verdict for a worksheet item (browser-verify run).
export const AutoVerdict = ({ execution }) => {
  if (!execution) return null;
  const color = AUTO_VERDICT_COLOR[execution.verdict] || "var(--bss-fg-subtle)";
  const verdictLabel = String(execution.verdict || "").replace(/_/g, " ");
  return (
    <div style={{ marginTop: 6 }}>
      <span
        title={execution.notes || ""}
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 0.4,
          color,
          border: `1px solid ${color}`,
          borderRadius: 4,
          padding: "1px 6px"
        }}
      >
        auto · {verdictLabel}
      </span>
      {/* Recency matters for trust: a verdict from a run against last week's
          branch state must not read as current. */}
      {execution.executedAt && (
        <span
          style={{
            color: "var(--bss-fg-subtle)",
            fontSize: 11,
            marginLeft: 6
          }}
        >
          verified {relativeTime(execution.executedAt)}
        </span>
      )}
      {execution.notes && (
        <div
          style={{
            color: "var(--bss-fg-muted)",
            fontSize: 12,
            marginTop: 4,
            whiteSpace: "pre-wrap"
          }}
        >
          {execution.notes}
        </div>
      )}
      {execution.videoFile && (
        <EvidenceVideo runId={execution.runId} file={execution.videoFile} />
      )}
    </div>
  );
};
